import React, {FC, useEffect, useState, useRef} from 'react';
import styled from 'styled-components';

import App from '../../../models/app';
import { SectionTitle } from '../Common';
import { translations, translateAll } from 'i18n';
import { borderColor, fontSemibold } from '../../../themes';
import { LabelName, LabelTexts, LabelType } from './types';
import { LABELS } from './config';
import { LabelPortal } from './LabelPortal';

interface LabelsProps {
  app: App;
  phoneAppVersion: string;
  deviceName: string;
}

interface LabelsState {
  labels: LabelType[];
  currentLabelName?: LabelName;
}

const LabelKeyContainer = styled.div`
  padding: 10px 20px;
  background-color: #E7E9EA;
  border-radius: 10px;
  margin-right: 10px;
`;

const ActiveLabelKeyContainer = styled.div`
  padding: 10px 20px;
  background-color: #00A0F3;
  border-radius: 10px;
  margin-right: 10px;
`;

const LabelsContainer = styled.div`
  display: flex;
  padding: 20px;
`;

const LabelsTitleContainer = styled.div`
  border-top: 1px solid ${borderColor};
  border-bottom: 1px solid ${borderColor};
`;

const LabelKeyText = styled.span`
  font-family: ${fontSemibold};
`;

const ActiveLabelKeyText = styled.span`
  font-family: ${fontSemibold};
  color: #FFFFFF;
`;

interface LabelProps {
  text: string;
  id: string;
  onClick: () => void;
  active?: boolean;
}

const Label: FC<LabelProps> = ({ text, onClick, id, active }) => {
  if (active) {
    return (
      <ActiveLabelKeyContainer id={id} aria-label={id} onClick={onClick}>
        <ActiveLabelKeyText>{text}</ActiveLabelKeyText>
      </ActiveLabelKeyContainer>
    );
  }

  return (
    <LabelKeyContainer id={id} aria-label={id} onClick={onClick}>
      <LabelKeyText>{text}</LabelKeyText>
    </LabelKeyContainer>
  );
}

class Labels extends React.Component<LabelsProps, LabelsState> {

  private translations: { [key: string]: LabelTexts } = {};
  private buttonTranslations: { [key: string]: any } = {};

  constructor(props: LabelsProps) {
    super(props);

    const labels = Labels.getActiveLabels(props.app);

    const {
      page: {
        [props.app.isClock ? 'Clock' : 'App']: {
          Labels: labelTranslations,
          buttons: buttonTranslations,
        },
      },
    } = translations;

    Object.keys(labelTranslations).forEach((key) => {
      this.translations[key] = translateAll(labelTranslations[key]);
    });

    this.buttonTranslations = buttonTranslations;

    this.state = {
      labels,
    };
  }

  static getDerivedStateFromProps(props: LabelsProps, state: LabelsState) {
    const oldLabels = [...state.labels];
    const newLabels = Labels.getActiveLabels(props.app);

    // both labels sets are empty, there is no need to change the state
    if (!oldLabels.length && !newLabels.length) {
      return null;
    }

    const labelKeys: LabelName[] = oldLabels.map(label => label.name);
    const newLabelKeys: LabelName[] = newLabels.map(label => label.name);

    if (JSON.stringify(labelKeys) === JSON.stringify(newLabelKeys)) {
      return null;
    }

    return {
      labels: newLabels,
    };
  }

  render() {
    const { app } = this.props;
    const { labels } = this.state;

    if (!labels.length) {
      return <React.Fragment />
    }

    return (
      <div>
        <div>
          {this.renderLabelDetails()}
        </div>
        <LabelsTitleContainer>
          <SectionTitle>Labels</SectionTitle>
        </LabelsTitleContainer>
        <LabelsContainer>
          {labels.map((label, index) => {
            const text = this.translations[label.translationKey(app)].key;
            return (
              <Label
                id={`label-open-${label.name.toLowerCase()}`}
                onClick={() => this.onLabelClick(label.name)}
                text={text}
                key={index}
              />
            )
          })}
        </LabelsContainer>
      </div>
    );
  }

  onLabelClick = (name: LabelName) => {
    this.setState({ currentLabelName: name });
  }

  onLabelDismiss = () => {
    this.setState({ currentLabelName: undefined });
  }

  private renderLabelDetails() {
    const { currentLabelName, labels } = this.state;
    const { app, phoneAppVersion, deviceName } = this.props;

    const labelProps = {
      show: false,
      title: '',
      message: '',
    };

    if (currentLabelName) {
      const currentLabel = labels.find(({ name }) => name === currentLabelName);
      if (currentLabel) {
        labelProps.show = true;
        const { labelTitle, labelMessage, translationKey } = currentLabel;
        const labelTranslations = this.translations[translationKey(app)];

        labelProps.title = labelTitle ?
          labelTitle(labelTranslations) :
          labelTranslations.title;
        labelProps.message = (labelMessage ?
          labelMessage(labelTranslations, app, { phoneAppVersion, deviceName }) :
          labelTranslations.message) as any;
      }
    }

    return (
      <LabelPortal
        onLabelDismiss={this.onLabelDismiss}
        cancelTranslation={this.buttonTranslations.cancel()}
        {...labelProps}
      />
    );
  }

  static getActiveLabels(app: App): LabelType[] {
    return LABELS.filter((label) => label.shouldDisplay(app)).sort();
  }

}

export interface ToggleItem <T> {
  value: T;
  text: string;
  isToggled: boolean;
}

export interface ToggleLabelsProps<T> {
  values: ToggleItem<T>[];
  onValuesChanged?: (item: ToggleItem<T>[]) => void;
}

export const ToggleLabels = <T extends unknown>(props: ToggleLabelsProps<T>)
    : React.ReactElement  => {
  const [items, setItems] = useState(props.values);
  const isMounted = useRef(false);

  useEffect(() => {
    if (isMounted.current) {
      /* Notify listeners when items states change. */
      if (props.onValuesChanged) {
        props.onValuesChanged(items);
      }
    } else {
      isMounted.current = true;
    }
  },[items]);

  const labels: React.ReactFragment[] = [];
  for (let i = 0 ; i < items.length; i++) {
    const item = items[i];
    labels.push((<Label
        id={`filter-label-id-${item.text}`}
        key={`filter-label-key-${item.text}`}
        text={item.text}
        onClick={() => {
          /* Set the new toggle state of the item. */
          item.isToggled = !item.isToggled;

          /* Reset the items to trigger an UI update. */
          setItems([...items]);
        }}
        active={item.isToggled}
      />));
  }

  return (<LabelsTitleContainer>
    <LabelsContainer>
      { ...labels }
    </LabelsContainer>
  </LabelsTitleContainer>);
}

export default Labels;
